import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import StorePickupPackageCommand from '../../../../../../services/command/storePickupPackage/StorePickupPackageCommand.services';
import { SCUIButton, SCUILoadingLinear, SCUIDateRangerV2 } from '../../../../../../atomic/atoms';
import { Box, Button, TextField, Typography } from '@mui/material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUICustomDataGrid } from '@/atomic/molecules';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import ClearIcon from '@mui/icons-material/Clear';
import { Region } from '../../../../../../types/enums/region-enum';
import { format, subDays } from 'date-fns';
import { SPP } from '../../../../storePickupPackage.classes';

export const ReportHistory = () => {
    const userStoreCode = localStorage.getItem('storeCode') || '';
    const countryId: any = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId?.toString();

    const { t } = useTranslation();
    const location = useLocation();

    const today = format(new Date(), 'dd-MM-yyyy');
    const tenDaysAgo = format(subDays(new Date(), 10), 'dd-MM-yyyy');

    const [reportHistory, setReportHistory] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [startDate, setStartDate] = useState<string>(tenDaysAgo);
    const [endDate, setEndDate] = useState<string>(today);
    const [shippingListNo, setShippingListNo] = useState<string>('');
    const [storeCode, setStoreCode] = useState<string>(userStoreCode || '');

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab === '1') {
            getReportHistory();
        }
    }, [location.search]);

    const columns: ExtendedGridColDef[] = [
        {
            field: 'dfsDocumentNumber',
            headerName: t('storePickupPackageAdmission.dfsDocumentNumber'),
            flex: 1,
            renderCell: (params: any) => {
                return (
                    <Typography
                        className={`${SPP.cellDfsNumber} ${SPP.cellDfsNumberLink}`}
                        data-dfs-number={params.value}
                        sx={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
                        onClick={() => handlePrint(params.row)}
                    >
                        {params.value}
                    </Typography>
                );
            },
        },
        {
            field: 'documentCreationDate',
            headerName: t('storePickupPackageAdmission.documentCreationDate'),
            flex: 1,
            renderCell: (params: any) => {
                if (!params.value) return '';
                try {
                    const date = new Date(params.value);
                    return (
                        <span className={SPP.cellDocumentDate} data-document-date={params.value}>
                            {format(date, 'dd-MM-yyyy HH:mm')}
                        </span>
                    );
                } catch {
                    return (
                        <span className={SPP.cellDocumentDate} data-document-date={params.value}>
                            {params.value}
                        </span>
                    );
                }
            },
        },
        {
            field: 'operatorName',
            headerName: t('storePickupPackageAdmission.operatorName'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPP.cellOperatorName} data-operator-name={params.row.operatorName}>
                    {params.row.operatorName}
                </span>
            ),
        },
        {
            field: 'shippingListNo',
            headerName: t('storePickupPackageAdmission.shippingListNo'),
            flex: 1,
            renderCell: (params: any) => {
                const shippingListNoValue = params.row.shippingListNo;

                if (!shippingListNoValue) {
                    return (
                        <span className={SPP.cellShippingListNo} data-shipping-list-no={shippingListNoValue}>
                            {shippingListNoValue}
                        </span>
                    );
                }

                return (
                    <a
                        target="_blank"
                        rel="noreferrer"
                        className={`${SPP.cellShippingListNo} ${SPP.cellShippingListNoLink}`}
                        data-shipping-list-no={shippingListNoValue}
                        href={`/orderdetail?shippingListNo=${shippingListNoValue}`}
                    >
                        {shippingListNoValue}
                    </a>
                );
            },
        },
    ];

    const handlePrint = (row: any) => {
        setLoading(true);

        let cultureCode = 'tr-TR';

        if (+countryId === Region.Kazakhistan) {
            cultureCode = 'kk-KZ';
        }

        StorePickupPackageCommand.PrintReport({
            dfsDocumentNumber: row.dfsDocumentNumber,
            cultureCode,
            countryId: +countryId,
        })
            .then((response: any) => {
                if (!response) {
                    Swal.fire(t('general.anErrorOccured'), '', 'error');
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const getReportHistory = () => {
        if (!startDate || !endDate) return;

        setLoading(true);

        // Backend'e göndermek için tarihleri yyyy-MM-dd formatına çevir
        const formatForBackend = (dateStr: string) => {
            const [day, month, year] = dateStr.split('-');
            return `${year}-${month}-${day}`;
        };

        StorePickupPackageCommand.GetReportHistory({
            startDate: formatForBackend(startDate),
            endDate: formatForBackend(endDate),
            shippingListNo,
            storeCode,
            countryId: +countryId,
        })
            .then((res: any) => {
                const dataWithId = (res?.data?.items || []).map((item: any, index: number) => ({
                    id: index + 1,
                    ...item,
                }));
                setReportHistory(dataWithId || []);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDate = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    const handleSearch = () => {
        getReportHistory();
    };

    const handleClear = () => {
        const today = format(new Date(), 'dd-MM-yyyy');
        const tenDaysAgo = format(subDays(new Date(), 10), 'dd-MM-yyyy');
        setStartDate(tenDaysAgo);
        setEndDate(today);
        setShippingListNo('');
        setStoreCode(userStoreCode || '');
    };

    return (
        <Box className={SPP.history}>
            <Box className={SPP.historyLoading} sx={{ mb: 2 }}>
                <SCUILoadingLinear display={loading} />
            </Box>
            <Box
                className={SPP.historyFilters}
                sx={{ mb: 4, display: 'flex', alignItems: 'center', flexDirection: 'row' }}
            >
                <Box className={`${SPP.historyDateRangeBox} ${SPP.inputHistoryDateRange}`} sx={{ flex: 1 }}>
                    <SCUIDateRangerV2
                        fromDate={startDate}
                        toDate={endDate}
                        setDateRange={handleDate}
                        dateFormat="DD-MM-YYYY"
                        fullWidth
                    />
                </Box>
                <TextField
                    label={t('storePickupPackageAdmission.shippingListNo')}
                    value={shippingListNo}
                    onChange={(e) => setShippingListNo(e.target.value)}
                    sx={{ ml: 2 }}
                    className={SPP.inputHistoryShippingListNo}
                />
                <TextField
                    label={t('storePickupPackageAdmission.storeCode')}
                    value={storeCode || ''}
                    onChange={(e) => setStoreCode(e.target.value)}
                    disabled={!!userStoreCode}
                    sx={{ ml: 2, mr: 2 }}
                    className={SPP.inputHistoryStoreCode}
                />
                <SCUIButton
                    text={t('general.filter')}
                    onClick={handleSearch}
                    type="primary"
                    sizeType="default"
                    size="s"
                    className={SPP.btnHistorySearch}
                />
                {(startDate || endDate || shippingListNo || (storeCode && storeCode !== userStoreCode)) && (
                    <Button
                        onClick={() => handleClear()}
                        className={SPP.btnHistoryClear}
                        sx={{
                            minWidth: '30px !important',
                            width: '30px !important',
                            minHeight: '30px !important',
                            height: '30px !important',
                            backgroundColor: 'red',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'none',
                            p: 0,
                            ml: 2,
                            '&:hover': {
                                backgroundColor: 'red',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <ClearIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                )}
            </Box>
            <Box className={SPP.historyGrid}>
                <SCUICustomDataGrid columns={columns} rows={reportHistory || []} allrecord hideFooter />
            </Box>
        </Box>
    );
};
